/**
 * Shared market pricing — single source of truth for Delivery Guidelines
 * and the Delivery Fee Calculator.
 */

export const MARKETS = {
  local: {
    id: 'local',
    name: 'Local',
    guidelinesName: 'Nashville',
    meta: 'Local · ≤ 30 mi',
    maxDistance: 30,
    rentalMinimum: 500,
    feePercentage: 0.1,
    minFee: 250,
    freeThreshold: 5000,
    standardNights: 0,
    standardDays: 1,
    samples: null,
    timing: 'Same-day delivery & strike',
    /** Collapsed Guidelines summary (orientation only). */
    collapsedMinimumLabel: '$250 minimum',
    /** Expanded Guidelines qualification (full Nashville rule). */
    minimumLabel: '$250 minimum if under $5,000',
    rateLabel: '10% of invoice',
    complimentaryLabel: '$5,000+',
  },
  regional: {
    id: 'regional',
    name: 'Regional',
    guidelinesName: 'Regional Markets',
    meta: '≤ 300 mi',
    maxDistance: 300,
    rentalMinimum: 3500,
    feePercentage: 0.1,
    minFee: 750,
    freeThreshold: 18000,
    standardNights: 1,
    standardDays: 2,
    samples: 'Atlanta, Birmingham, Louisville, Chattanooga, Huntsville, Memphis',
    timing: 'Up to 2 days / 1 night for travel; delivery & strike same day',
    collapsedMinimumLabel: '$750 minimum',
    minimumLabel: '$750 minimum',
    rateLabel: '10% of invoice',
    complimentaryLabel: '$18,000+',
  },
  extended: {
    id: 'extended',
    name: 'Extended',
    guidelinesName: 'Extended Markets',
    meta: '300–600 mi',
    maxDistance: 600,
    rentalMinimum: 10000,
    feePercentage: 0.15,
    minFee: 1500,
    freeThreshold: 25000,
    standardNights: 2,
    standardDays: 3,
    samples: 'Charleston, Savannah, New Orleans',
    timing: 'Up to 3 days / 2 nights for travel; delivery & strike same day',
    collapsedMinimumLabel: '$1,500 minimum',
    minimumLabel: '$1,500 minimum',
    rateLabel: '15% of invoice',
    complimentaryLabel: '$25,000+',
  },
  stretch: {
    id: 'stretch',
    name: 'Stretch',
    guidelinesName: 'Long-Haul Markets',
    meta: '600+ mi',
    maxDistance: Infinity,
    rentalMinimum: 20000,
    feePercentage: null,
    minFee: null,
    freeThreshold: null,
    standardNights: null,
    standardDays: null,
    samples: 'NYC, California, and other long-haul markets',
    timing: 'Timing TBD',
    collapsedMinimumLabel: 'Custom pricing',
    minimumLabel: null,
    rateLabel: 'Custom pricing based on the actual resources required to execute the project.',
    complimentaryLabel: 'No standard threshold',
    customPricingDetail:
      'Pricing considers the actual project-specific costs required for delivery, which may include transportation, crew time, travel, lodging, and other logistics needed to complete the project.',
  },
};

export const MARKET_ORDER = ['local', 'regional', 'extended', 'stretch'];

export const MULTI_DAY_COMPLIMENTARY_NOTE =
  'Complimentary delivery thresholds apply to standard single-day delivery only and do not apply to multi-day events.';

/** Overnight nights beyond the market standard, charged per crew member. */
export const EXTENDED_OVERNIGHT_RATE_PER_MEMBER = 200;

/**
 * Resolve market tier from driving distance (miles from Nashville HQ).
 */
export function determineMarketByDistance(distance) {
  if (distance == null || Number.isNaN(distance)) return null;
  if (distance <= MARKETS.local.maxDistance) return MARKETS.local;
  if (distance <= MARKETS.regional.maxDistance) return MARKETS.regional;
  if (distance <= MARKETS.extended.maxDistance) return MARKETS.extended;
  return MARKETS.stretch;
}

/**
 * Complimentary Delivery Threshold applies only to qualifying standard single-day deliveries.
 * Multi-day events never receive complimentary delivery based on the threshold alone.
 */
export function isComplimentaryDeliveryEligible(productTotal, market, eventDays = 1) {
  if (!market || market.freeThreshold == null || market.feePercentage == null) return false;
  if (eventDays > 1) return false;
  return productTotal >= market.freeThreshold;
}

/**
 * Evaluate standard delivery fee for a market.
 * Returns waived only when single-day and product meets Complimentary Delivery Threshold.
 */
export function evaluateStandardDeliveryFee({ invoice, product, market, eventDays = 1 }) {
  if (!market) {
    return {
      standardFee: 0,
      amountToThreshold: null,
      freeThresholdApplied: null,
      feeFromPercentage: null,
      feeBasis: null,
      complimentaryEligible: false,
      complimentaryBlockedByMultiDay: false,
    };
  }

  const isStretch = market.feePercentage == null || market.name === 'Stretch';
  if (isStretch) {
    return {
      standardFee: null,
      amountToThreshold: null,
      freeThresholdApplied: null,
      feeFromPercentage: null,
      feeBasis: null,
      complimentaryEligible: false,
      complimentaryBlockedByMultiDay: false,
    };
  }

  const isMultiDay = eventDays > 1;
  const freeThresholdApplied = isMultiDay ? null : market.freeThreshold;
  const feeFromPercentage = invoice * market.feePercentage;

  if (!isMultiDay && product >= market.freeThreshold) {
    return {
      standardFee: 'waived',
      amountToThreshold: 0,
      freeThresholdApplied,
      feeFromPercentage,
      feeBasis: null,
      complimentaryEligible: true,
      complimentaryBlockedByMultiDay: false,
    };
  }

  const standardFee = Math.max(feeFromPercentage, market.minFee);
  return {
    standardFee,
    amountToThreshold:
      !isMultiDay && market.freeThreshold != null
        ? market.freeThreshold - product
        : null,
    freeThresholdApplied,
    feeFromPercentage,
    feeBasis: feeFromPercentage >= market.minFee ? 'percentage' : 'minimum',
    complimentaryEligible: false,
    complimentaryBlockedByMultiDay: isMultiDay,
  };
}

/**
 * Extended overnight fee for nights beyond the market's included travel nights.
 * Same formula as the Delivery Fee Calculator.
 */
export function evaluateExtendedOvernightFee({
  market,
  nights = 0,
  teamMembers = 0,
  overnightRequired = true,
}) {
  if (!overnightRequired || !market || market.standardNights == null) {
    return { extendedFee: 0, additionalNights: 0, explanation: null };
  }
  const nightsNum = Math.max(0, parseInt(nights, 10) || 0);
  const crew = Math.max(0, parseInt(teamMembers, 10) || 0);
  const additionalNights = Math.max(0, nightsNum - market.standardNights);
  if (additionalNights <= 0 || crew <= 0) {
    return { extendedFee: 0, additionalNights, explanation: null };
  }
  const extendedFee = additionalNights * crew * EXTENDED_OVERNIGHT_RATE_PER_MEMBER;
  const explanation =
    `${additionalNights} additional night${additionalNights !== 1 ? 's' : ''} × ` +
    `${crew} team member${crew !== 1 ? 's' : ''} × $${EXTENDED_OVERNIGHT_RATE_PER_MEMBER}`;
  return { extendedFee, additionalNights, explanation };
}

/** Guidelines display bands derived from shared MARKETS. */
export function getGuidelinesMarketBands() {
  return MARKET_ORDER.map((key) => {
    const m = MARKETS[key];
    return {
      id: m.id,
      name: m.guidelinesName,
      meta: m.meta,
      samples: m.samples,
      rentalMinimum: `$${m.rentalMinimum.toLocaleString('en-US')}`,
      rate: m.rateLabel,
      minimum: m.minimumLabel,
      complimentary: m.complimentaryLabel,
      timing: m.timing,
      collapsedMinimumLabel: m.collapsedMinimumLabel,
      customPricingDetail: m.customPricingDetail || null,
      isCustom: m.feePercentage == null,
    };
  });
}
